import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  ReceiptText,
  HandCoins,
  TrendingDown,
  Landmark,
  LogOut,
  Wallet,
  Repeat,
  Coins,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import api from "@/lib/api";

const baseLinks = [
  { to: "/", label: "Panel", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/cekler", label: "Çekler", icon: ReceiptText, testid: "nav-cheques", section: "cheques" },
  { to: "/alacaklar", label: "Alacaklar", icon: HandCoins, testid: "nav-receivables", section: "receivables" },
  { to: "/giderler", label: "Giderler", icon: TrendingDown, testid: "nav-expenses", section: "expenses" },
  { to: "/tekrarlayanlar", label: "Tekrarlayanlar", icon: Repeat, testid: "nav-recurring", section: "recurring" },
  { to: "/kasa", label: "Merkez Kasa", icon: Coins, testid: "nav-cash", section: "cash" },
  { to: "/kasa-bagcilar", label: "Bağcılar Kasa", icon: Coins, testid: "nav-cash-bagcilar", section: "cash_bagcilar" },
  { to: "/hesaplar", label: "Banka Hesapları", icon: Landmark, testid: "nav-bank-accounts", section: "bank_accounts" },
];

const adminLink = { to: "/admin", label: "Kullanıcılar", icon: ShieldCheck, testid: "nav-admin" };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const visibleBase = baseLinks.filter((l) => !l.section || hasPermission(user, l.section, "view"));
  const links = user?.role === "admin" ? [...visibleBase, adminLink] : visibleBase;
  const [pending, setPending] = useState({ main: 0, bagcilar: 0 });

  useEffect(() => {
    if (user?.role !== "admin") return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/cash/requests/pending-count");
        if (!cancelled) setPending({ main: data.main || 0, bagcilar: data.bagcilar || 0 });
      } catch {}
    };
    load();
    const t = setInterval(load, 20000);
    // Also refresh on window focus for snappier updates
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [user?.role]);

  const badgeFor = (to) => {
    if (user?.role !== "admin") return 0;
    if (to === "/kasa") return pending.main;
    if (to === "/kasa-bagcilar") return pending.bagcilar;
    return 0;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F9F8F6]">
      {/* Sidebar */}
      <aside className="w-64 bg-[#F0EFEA] border-r border-[#E6E4DD] flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-[#E6E4DD]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#8A5A44] flex items-center justify-center">
              <Wallet size={20} strokeWidth={1.75} className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg text-[#2D2C2A] leading-tight">Nakit Akışı</h1>
              <p className="text-xs text-[#5C5B57]">Finans Paneli</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {links.map(({ to, label, icon: Icon, testid, end }) => {
            const badge = badgeFor(to);
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-white text-[#8A5A44] shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                      : "text-[#5C5B57] hover:bg-white/60"
                  }`
                }
              >
                <Icon size={18} strokeWidth={1.75} />
                <span className="flex-1">{label}</span>
                {badge > 0 && (
                  <span
                    data-testid={`${testid}-badge`}
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#B24C4C] text-white text-[10px] font-bold"
                  >
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#E6E4DD]">
          <div className="px-2 pb-3">
            <p className="text-xs text-[#5C5B57]">Giriş yapan</p>
            <p className="text-sm font-medium text-[#2D2C2A] truncate" data-testid="current-user-name">
              {user?.name}
            </p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#5C5B57] hover:text-[#B24C4C] hover:bg-white rounded-xl transition-colors"
          >
            <LogOut size={16} strokeWidth={1.75} /> Çıkış Yap
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
